import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Menu, X, Share2, Twitter, Facebook, Linkedin, Mail } from 'lucide-react';
import { DiscussionEmbed } from 'disqus-react';
import './InfoPage.css';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author_name: string;
  category: string;
  published_at: string;
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
}

const BlogPostDetail: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [disqusLoaded, setDisqusLoaded] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  // Reset Disqus when slug changes
  useEffect(() => {
    setDisqusLoaded(false);
    const timer = setTimeout(() => {
      setDisqusLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/slug/${slug}`);
      const data = await response.json();

      if (data.success && data.data) {
        setPost(data.data);

        // Update page title and meta description for SEO
        if (data.data.meta_title) {
          document.title = data.data.meta_title;
        }
        if (data.data.meta_description) {
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute('content', data.data.meta_description);
          }
        }
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Convert markdown to HTML (basic implementation - you can enhance this with a markdown library)
  const renderContent = (content: string) => {
    // For now, we'll use dangerouslySetInnerHTML with basic markdown-to-HTML
    // In production, consider using a library like react-markdown or marked
    let html = content
      .replace(/### (.*)/g, '<h3>$1</h3>')
      .replace(/## (.*)/g, '<h2>$1</h2>')
      .replace(/# (.*)/g, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/---/g, '<hr />')
      .replace(/\n\n/g, '</p><p>')
      .replace(/- (.*)/g, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    return `<p>${html}</p>`;
  };

  // Disqus configuration
  const disqusShortname = 'homequesttech-com';
  const disqusConfig = {
    url: `https://www.homequesttech.com/blog/${slug}`,
    identifier: slug,
    title: post?.title || ''
  };

  // Social sharing functions
  const shareUrl = `https://www.homequesttech.com/blog/${slug}`;
  const shareTitle = post?.title || '';

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const shareViaEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check out this article: ${shareUrl}`)}`;
  };

  if (loading) {
    return (
      <div className="info-page">
        <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="info-page">
        <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Post Not Found</h2>
          <p style={{ marginBottom: '2rem' }}>The blog post you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/blog')} className="cta-button">
            <ArrowLeft style={{ width: '20px', height: '20px' }} /> Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-content" style={{ paddingTop: '6rem' }}>
        <div className="content-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Back button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/blog')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: '#60A5FA',
              fontSize: '1rem',
              cursor: 'pointer',
              marginBottom: '2rem',
              padding: '0.5rem 0'
            }}
          >
            <ArrowLeft style={{ width: '20px', height: '20px' }} />
            Back to Blog
          </motion.button>

          {/* Post header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div style={{ display: 'inline-block', padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '6px', fontSize: '0.875rem', color: '#60A5FA', marginBottom: '1rem' }}>
              {post.category}
            </div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#F9FAFB', marginBottom: '1.5rem', lineHeight: '1.2' }}>
              {post.title}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '3rem', paddingBottom: '2rem', borderBottom: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar style={{ width: '16px', height: '16px' }} />
                {formatDate(post.published_at)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User style={{ width: '16px', height: '16px' }} />
                {post.author_name}
              </div>
            </div>
          </motion.div>

          {/* Post content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#D1D5DB',
              marginBottom: '3rem'
            }}
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
          />

          {/* Social Sharing Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            style={{
              marginBottom: '4rem',
              paddingBottom: '2rem',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Share2 style={{ width: '18px', height: '18px', color: '#9CA3AF' }} />
              <h3 style={{ fontSize: '1rem', color: '#9CA3AF', margin: 0 }}>Share this article:</h3>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={shareOnTwitter}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  background: 'rgba(29, 161, 242, 0.1)',
                  border: '1px solid rgba(29, 161, 242, 0.3)',
                  borderRadius: '8px',
                  color: '#1DA1F2',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(29, 161, 242, 0.2)';
                  e.currentTarget.style.borderColor = '#1DA1F2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(29, 161, 242, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(29, 161, 242, 0.3)';
                }}
              >
                <Twitter style={{ width: '16px', height: '16px' }} />
                Twitter
              </button>

              <button
                onClick={shareOnFacebook}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  background: 'rgba(24, 119, 242, 0.1)',
                  border: '1px solid rgba(24, 119, 242, 0.3)',
                  borderRadius: '8px',
                  color: '#1877F2',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(24, 119, 242, 0.2)';
                  e.currentTarget.style.borderColor = '#1877F2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(24, 119, 242, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(24, 119, 242, 0.3)';
                }}
              >
                <Facebook style={{ width: '16px', height: '16px' }} />
                Facebook
              </button>

              <button
                onClick={shareOnLinkedIn}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  background: 'rgba(10, 102, 194, 0.1)',
                  border: '1px solid rgba(10, 102, 194, 0.3)',
                  borderRadius: '8px',
                  color: '#0A66C2',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(10, 102, 194, 0.2)';
                  e.currentTarget.style.borderColor = '#0A66C2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(10, 102, 194, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(10, 102, 194, 0.3)';
                }}
              >
                <Linkedin style={{ width: '16px', height: '16px' }} />
                LinkedIn
              </button>

              <button
                onClick={shareViaEmail}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  background: 'rgba(156, 163, 175, 0.1)',
                  border: '1px solid rgba(156, 163, 175, 0.3)',
                  borderRadius: '8px',
                  color: '#9CA3AF',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(156, 163, 175, 0.2)';
                  e.currentTarget.style.borderColor = '#9CA3AF';
                  e.currentTarget.style.color = '#D1D5DB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(156, 163, 175, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.3)';
                  e.currentTarget.style.color = '#9CA3AF';
                }}
              >
                <Mail style={{ width: '16px', height: '16px' }} />
                Email
              </button>
            </div>
          </motion.div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ marginBottom: '4rem', paddingBottom: '2rem', borderBottom: '1px solid rgba(59, 130, 246, 0.2)' }}
            >
              <h3 style={{ fontSize: '1rem', color: '#9CA3AF', marginBottom: '1rem' }}>Tags:</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '0.375rem 0.75rem',
                      background: 'rgba(31, 41, 55, 0.6)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      color: '#9CA3AF'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Disqus Comments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{ marginTop: '4rem' }}
          >
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#F9FAFB', marginBottom: '2rem' }}>
              Comments
            </h2>
            {disqusLoaded && (
              <DiscussionEmbed
                key={slug}
                shortname={disqusShortname}
                config={disqusConfig}
              />
            )}
          </motion.div>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/about')}>About</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostDetail;
